import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

function SeniorManagementDetail() {
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [otherMembers, setOtherMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOtherMembers, setLoadingOtherMembers] = useState(false);

  useEffect(() => {
    fetchMember();
    fetchOtherMembers();
  }, [id]);

  const fetchMember = async () => {
    try {
      const response = await axios.get(`/api/senior-management/${id}`);
      setMember(response.data);
    } catch (error) {
      console.error('Error fetching member:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOtherMembers = async () => {
    setLoadingOtherMembers(true);
    try {
      const response = await axios.get('/api/senior-management');
      const allMembers = response.data.management || [];
      const filtered = allMembers.filter((item) => String(item.id) !== String(id)).slice(0, 6);
      setOtherMembers(filtered);
    } catch (error) {
      console.error('Error fetching other management members:', error);
    } finally {
      setLoadingOtherMembers(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h4>Member Not Found</h4>
          <p>The management member you are looking for does not exist.</p>
          <Link to="/" className="btn btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  const profileImage = member.image || '/img/Male Professional Headshot - East African.jpeg';

  return (
    <div className="container py-5">
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/" className="text-decoration-none" style={{ color: 'var(--tgh-blue-light)' }}>
              Home
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {member.name}
          </li>
        </ol>
      </nav>

      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div className="col-lg-8">
          <div className="senior-management-detail-content">
          <h1 className="mb-3" style={{ color: 'var(--tgh-blue)', fontSize: '2.5rem' }}>
            {member.name}
          </h1>
          <h3 className="text-muted mb-4" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
            {member.designation}
          </h3>

          <div className="senior-management-image-wrap">
            <div className="senior-management-image-frame">
              <img
                src={profileImage}
                alt={member.name}
                className="img-fluid rounded"
                onError={(e) => {
                  e.target.src = '/img/Male Professional Headshot - East African.jpeg';
                }}
              />
            </div>
          </div>
          
          {member.bio && (
            <div className="mb-4">
              <h4 className="mb-3">Profile</h4>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#333', textAlign: 'justify' }}>
                {member.bio}
              </p>
            </div>
          )}

          {(member.facebook || member.twitter || member.linkedin || member.instagram) && (
            <div className="mt-4">
              <h5 className="mb-3">Connect</h5>
              <div className="d-flex gap-3">
                {member.facebook && (
                  <a
                    href={member.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                    style={{ fontSize: '2rem', color: '#1877f2' }}
                  >
                    <i className="bi bi-facebook"></i>
                  </a>
                )}
                {member.twitter && (
                  <a
                    href={member.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                    style={{ fontSize: '2rem', color: '#1da1f2' }}
                  >
                    <i className="bi bi-twitter"></i>
                  </a>
                )}
                {member.linkedin && (
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                    style={{ fontSize: '2rem', color: '#0077b5' }}
                  >
                    <i className="bi bi-linkedin"></i>
                  </a>
                )}
                {member.instagram && (
                  <a
                    href={member.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                    style={{ fontSize: '2rem', color: '#e4405f' }}
                  >
                    <i className="bi bi-instagram"></i>
                  </a>
                )}
              </div>
            </div>
          )}
          <div style={{ clear: 'both' }}></div>
          </div>
        </div>

        <div className="col-lg-4 mt-4 mt-lg-0">
          <div className="more-news-sidebar">
            <div className="card" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid var(--tgh-blue-light)', borderRadius: '8px 8px 0 0' }}>
                <h4 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a' }}>
                  Other Senior Management
                </h4>
              </div>
              <div className="card-body p-3" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                {loadingOtherMembers ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : otherMembers.length === 0 ? (
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>No other management members available</p>
                ) : (
                  <div className="list-group list-group-flush">
                    {otherMembers.map((item) => (
                      <Link
                        key={item.id}
                        to={`/senior-management/${item.id}`}
                        className="list-group-item list-group-item-action"
                        style={{
                          border: 'none',
                          borderBottom: '1px solid #e0e0e0',
                          padding: '1rem 0',
                          textDecoration: 'none',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div className="d-flex align-items-start gap-3">
                          <div style={{ width: '70px', height: '70px', flexShrink: 0, overflow: 'hidden', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                            <img
                              src={item.image || '/img/Male Professional Headshot - East African.jpeg'}
                              alt={item.name}
                              style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'top center' }}
                              onError={(e) => {
                                e.target.src = '/img/Male Professional Headshot - East African.jpeg';
                              }}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1" style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1a1a1a', lineHeight: '1.3' }}>
                              {item.name}
                            </h6>
                            <small className="text-muted" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                              {item.designation}
                            </small>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
                <div className="mt-3 text-center">
                  <Link
                    to="/senior-management"
                    className="btn btn-outline-primary btn-sm"
                    style={{ borderRadius: '4px', fontSize: '0.9rem' }}
                  >
                    View All <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SeniorManagementDetail;
